// ═══════════════════════════════════════════════════════════════════════════
// Lectura desde Postgres con caché local — Etapa 4a
//
// REGLA ACORDADA: la caché local manda SIEMPRE; Postgres actualiza en segundo
// plano. Nunca se bloquea la app esperando la red.
//
// Consecuencias deliberadas:
//   · Arranque instantáneo, con o sin conexión.
//   · Un Postgres caído o lento NO impide trabajar: se sigue con lo último
//     conocido y se reintenta después.
//   · A cambio, los datos pueden estar unos minutos viejos. Para catálogo
//     (productos, clientes, precios) eso es aceptable; para folios y timbrado
//     NO lo sería — y por eso esos siguen sin tocarse en esta fase.
//
// El INTERRUPTOR (`fuenteDeDatos`) permite apagar esto sin desplegar. Si la
// lectura desde Postgres se comporta raro en producción, se vuelve a local en
// segundos desde Config.
// ═══════════════════════════════════════════════════════════════════════════

import { supabase } from "../auth/sesion.js";
import { clienteDesdePG, productoDesdePG, preciosDesdePG,
         preciosClienteDesdePG, remisionDesdePG } from "./adaptadores.js";

const CLAVE_CACHE  = "vt-pg-cache";

// ── Ya no hay interruptor ──────────────────────────────────────────────────
//
// Postgres es la fuente, punto. El interruptor existió para migrar sin riesgo y
// cumplió: con los datos ya allá, mantener dos modos solo servía para que la
// misma pantalla contara dos verdades distintas según el dispositivo.
//
// Se conserva la función porque medio código pregunta por ella, y devolver
// siempre 'postgres' es más honesto que borrarla y dejar ramas muertas. La
// limpieza de las llamadas es aparte y no urge.
export const fuenteDeDatos = () => "postgres";

// ── Caché ──────────────────────────────────────────────────────────────────

export function cacheLocal() {
  try {
    const c = JSON.parse(localStorage.getItem(CLAVE_CACHE) || "null");
    return c && Array.isArray(c.clients) ? c : null;
  } catch { return null; }
}

function guardarCache(datos) {
  try { localStorage.setItem(CLAVE_CACHE, JSON.stringify(datos)); }
  catch (e) { console.warn("[lectura] no se pudo guardar la caché:", e?.message); }
}

/** Antigüedad de la caché en minutos, o null si no hay. */
export function edadCacheMinutos() {
  const c = cacheLocal();
  if (!c?._traidoEn) return null;
  return Math.round((Date.now() - new Date(c._traidoEn).getTime()) / 60000);
}

// ── Traer de Postgres ──────────────────────────────────────────────────────

/**
 * Lee catálogo y precios de Postgres y los deja con la forma de la app.
 * Lanza si falla: quien llama decide qué hacer (aquí, seguir con la caché).
 */
export async function traerDePostgres() {
  const [clientes, productos, listas, renglones, especiales, existencias,
         remisiones, lineas, cfdis, pagos, reps, saldos, comisiones, folios] =
    await Promise.all([
      sel("client", "*, price_list:price_list_id (id, code, label)"),
      sel("product", "*"),
      sel("price_list", "*"),
      sel("price_list_item", "*"),
      sel("client_price", "*"),
      sel("stock_actual", "product_id, qty"),
      // Las remisiones ya no salen del snapshot local. Mientras salían de ahí,
      // TODOS veían TODAS — un vendedor incluido. La RLS filtraba por cartera
      // desde hace semanas y no servía de nada porque nadie preguntaba.
      sel("remision", "*"),
      sel("remision_line", "*"),
      sel("cfdi_remision", "remision_id, cfdi:cfdi_id (facturapi_id, uuid_sat, status, total, verification_url, cancel_motive)"),
      sel("payment", "*"),
      // Los complementos de pago emitidos, para poder verlos y bajarlos.
      sel("cfdi_pago", "payment_id, cfdi:cfdi_id (facturapi_id, uuid_sat, status, verification_url)"),
      // La cuenta corriente de cada cliente. Se calcula en la base sumando
      // movimientos, así que llega ya resuelta y no hay dos versiones.
      sel("saldo_cliente", "client_id, recibido, aplicado, a_favor, por_cobrar"),
      // El estado de la comisión viaja con la remisión: el vendedor tiene que
      // poder ver de un vistazo, en CUALQUIER remisión, si está acreditada,
      // por acreditarse o liquidada. La vista ya lo calcula y la RLS la filtra.
      sel("comision_remision", "remision_id, comision, exigible, pagada_al_vendedor"),
      // El folio sale del CONTADOR, no de las remisiones visibles. Un vendedor
      // ve solo su cartera: calcularlo de lo que ve daría un número muy por
      // debajo del real y la app anunciaría un folio ya usado.
      sel("folio_sequence", "doc_type, serie, current_value"),
    ]);

  const legadoPorUuid = new Map(productos.map(p => [p.id, p.legacy_id || p.id]));
  // El mismo puente para clientes. Hace falta porque las remisiones viejas
  // guardaron su snapshot SIN `legacy_id`, y sin esto Cuentas no lograba
  // emparejarlas con su cliente. Ver `remisionDesdePG`.
  const legadoClientePorUuid = new Map(clientes.map(c => [c.id, c.legacy_id || c.id]));

  // El stock ya no es columna: se suma por producto sobre todos los almacenes.
  const stockPorProducto = new Map();
  for (const e of existencias) {
    const n = Number(e.qty) || 0;
    stockPorProducto.set(e.product_id, (stockPorProducto.get(e.product_id) || 0) + n);
  }

  const preciosPorCliente = preciosClienteDesdePG(especiales, legadoPorUuid);

  // Se agrupa una vez por remisión en vez de recorrer todo por cada una: con
  // años de historia, lo segundo se vuelve cuadrático y se nota.
  const lineasPorRem = new Map();
  for (const l of lineas) {
    if (!lineasPorRem.has(l.remision_id)) lineasPorRem.set(l.remision_id, []);
    lineasPorRem.get(l.remision_id).push(l);
  }
  const pagosPorRem = new Map();
  for (const p of pagos) {
    if (!pagosPorRem.has(p.remision_id)) pagosPorRem.set(p.remision_id, []);
    pagosPorRem.get(p.remision_id).push(p);
  }
  // El CFDI vigente manda; si solo hay cancelados, se muestra el cancelado.
  const cfdiPorRem = new Map();
  for (const cr of cfdis) {
    if (!cr.cfdi) continue;
    const previo = cfdiPorRem.get(cr.remision_id);
    if (!previo || cr.cfdi.status === "vigente") cfdiPorRem.set(cr.remision_id, cr.cfdi);
  }

  const comisionPorRem = new Map(comisiones.map(c => [c.remision_id, c]));

  // El complemento de cada pago, por el uuid del pago (no por su legacy_id:
  // `cfdi_pago` apunta a la fila, que es la identidad estable).
  const saldoPorCliente = new Map(saldos.map(s => [s.client_id, s]));

  //
  // Un pago puede tener VARIAS filas desde la 0063: cada complemento que se le
  // canceló deja la suya. Manda el vigente; si solo hay cancelados, se enseña
  // el cancelado —para que `pagosSinREP` vuelva a pedir el que falta—. El
  // mismo criterio que `cfdiPorRem` arriba.
  const repPorPago = new Map();
  for (const x of reps) {
    if (!x.cfdi) continue;
    const previo = repPorPago.get(x.payment_id);
    if (previo && previo.status === "vigente") continue;
    repPorPago.set(x.payment_id, {
      facturapiId: x.cfdi.facturapi_id, uuid: x.cfdi.uuid_sat,
      status: x.cfdi.status, url: x.cfdi.verification_url,
    });
  }

  return {
    clients: clientes.map(fila => {
      const c = clienteDesdePG(fila);
      const suyos = preciosPorCliente.get(fila.id);
      if (suyos && Object.keys(suyos).length) c.customPrices = suyos;
      const s = saldoPorCliente.get(fila.id);
      if (s) {
        c.aFavor = Number(s.a_favor) || 0;
        c.porCobrar = Number(s.por_cobrar) || 0;
      }
      return c;
    }),
    products: productos.map(p => productoDesdePG(p, stockPorProducto)),
    pricingRules: preciosDesdePG(listas, renglones, legadoPorUuid),
    invoices: remisiones.map(r => {
      const inv = remisionDesdePG(r, {
        lineas: lineasPorRem.get(r.id) || [],
        cfdi: cfdiPorRem.get(r.id) || null,
        pagos: pagosPorRem.get(r.id) || [],
        legadoPorUuid, legadoClientePorUuid, repPorPago,
      });
      const c = comisionPorRem.get(r.id);
      if (c) {
        inv.comision = Number(c.comision) || 0;
        inv.comisionExigible = !!c.exigible;
        inv.comisionLiquidada = !!c.pagada_al_vendedor;
      }
      return inv;
    }),
    siguienteFolio: siguienteFolioDe(folios),
    _traidoEn: new Date().toISOString(),
  };
}

/**
 * El SIGUIENTE folio a partir de la secuencia del servidor.
 *
 * ⚠ `current_value` es EL ÚLTIMO FOLIO USADO, no el siguiente. `next_folio()`
 *   (migración 0001, revisada en la 0010) incrementa Y LUEGO devuelve:
 *
 *       update folio_sequence set current_value = current_value + 1
 *        where ... returning current_value into v;
 *
 *   Así que al terminar, `current_value` vale exactamente el folio que se
 *   acaba de entregar.
 *
 * Aquí se devolvía `current_value` tal cual, o sea el número YA USADO. La app
 * anunciaba el folio anterior y al guardar la primera remisión tras cargar
 * salía «la remisión #N ya existe, ¿reemplazarla?» — ofreciendo pisar la venta
 * anterior. Medido en el respaldo del 10 de septiembre de 2026:
 *
 *       current_value = 423      max(folio) = 423
 *
 *   Los dos iguales: la prueba de que es el último usado y no el próximo.
 *
 * Por qué parecía intermitente: tras guardar, `aplicarRemisionGuardada` hace
 * `setFolio(inv.folio + 1)` con el folio REAL del servidor, así que el
 * contador se corregía solo. Fallaba únicamente en la primera remisión de cada
 * carga — y volvía a fallar en la siguiente sesión.
 *
 * Se saca a función propia y exportada para poder probarla: dentro de un
 * objeto literal de 200 líneas no había cómo.
 */
export function siguienteFolioDe(folios) {
  const f = (folios || []).find(x => x.doc_type === "remision" && !x.serie)
         || (folios || []).find(x => x.doc_type === "remision");
  if (!f || f.current_value == null) return null;
  const ultimo = Number(f.current_value);
  return Number.isFinite(ultimo) ? ultimo + 1 : null;
}

async function sel(tabla, columnas) {
  const { data, error } = await supabase.from(tabla).select(columnas);
  if (error) throw new Error(`${tabla}: ${error.message}`);
  return data || [];
}

// ── Orquestador ────────────────────────────────────────────────────────────

/**
 * Devuelve el catálogo SIN esperar a la red, y refresca en segundo plano.
 *
 * @param alActualizar  se invoca si la actualización trae algo distinto
 * @returns { datos, origen, edadMinutos }
 *          origen: 'cache' | 'postgres' | null (sin caché y sin red)
 */
export function leerCatalogo({ alActualizar, alFallar } = {}) {
  const cache = cacheLocal();

  // Refresco en segundo plano. Nunca lanza hacia afuera.
  (async () => {
    if (!navigator.onLine) return;
    try {
      const frescos = await traerDePostgres();
      const cambio = huella(cache) !== huella(frescos);
      guardarCache(frescos);
      if (cambio) alActualizar?.(frescos);
    } catch (e) {
      // Fallo de red, RLS o esquema: se sigue con la caché. Se avisa a quien
      // quiera mostrarlo, pero no se rompe nada.
      console.warn("[lectura] no se pudo refrescar desde Postgres:", e?.message);
      alFallar?.(e);
    }
  })();

  return {
    datos: cache,
    origen: cache ? "cache" : null,
    edadMinutos: edadCacheMinutos(),
  };
}

/**
 * ¿Cambió algo? Se compara TODO menos el sello de tiempo.
 *
 * ANTES ERA UNA HUELLA LIGERA —cuántos clientes, cuántos productos, suma de
 * totales y de precios— y el problema de una huella así es que lo que no mira,
 * no existe. Cualquier cambio en un campo ausente de la lista dejaba la
 * pantalla congelada: la caché en disco sí se actualizaba, así que el cambio
 * aparecía... a la SEGUNDA recarga. Un defecto casi imposible de reportar.
 *
 * Se descubrió al restaurar las fotos de los productos: el número de productos
 * no cambió, ni la suma de precios, así que la app decidió que no había
 * novedad y siguió pintando el catálogo sin fotos. El operador las tenía en la
 * base y no las veía en pantalla.
 *
 * Ya había pasado antes con las remisiones —el comentario "sin contar
 * remisiones, emitir una no refrescaba la pantalla" es el parche de aquella
 * vez—. Dos veces el mismo defecto es una señal: el arreglo no era agregar el
 * campo que faltaba, era dejar de llevar la lista.
 *
 * Comparar entero cuesta serializar ~430 KB en segundo plano. `guardarCache`
 * ya serializa lo mismo un renglón más abajo, así que el costo real es una
 * pasada más — barato al lado de un catálogo que miente.
 */
function huella(d) {
  if (!d) return null;
  // El sello de tiempo cambia en cada lectura; compararlo daría "cambió"
  // siempre y repintaríamos la pantalla sin motivo.
  const { _traidoEn, ...resto } = d;
  return JSON.stringify(resto);
}

/** Primera carga en un dispositivo: sin caché hay que ir a la red. */
export async function sembrarCache() {
  const frescos = await traerDePostgres();
  guardarCache(frescos);
  return frescos;
}

export function limpiarCache() {
  try { localStorage.removeItem(CLAVE_CACHE); } catch { /* nada que hacer */ }
}
